// ═══════════════════════════════════════
// EXPORT — Génération et téléchargement PDF (jspdf + svg2pdf)
// ═══════════════════════════════════════

import { Mp3Encoder } from '@breezystack/lamejs';

// Dimensions A4 en points (unité jsPDF)
const PAGE_W_PT = 595;
const PAGE_H_PT = 842;
const MARGIN_PT = 28;

// staffwidth pour le re-rendu print (pixels CSS).
// 1pt = 96/72 px → largeur utile ≈ (595 - 56) × 96/72 ≈ 719 px
const STAFF_W_PX = 719;

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Convertit les transform CSS inline en attributs SVG.
 * svg2pdf v2 ignore les transform dans style="...", il faut les passer en attribut.
 */
function _convertCssTransforms(svg) {
    svg.querySelectorAll('[style]').forEach(el => {
        const style = el.getAttribute('style') || '';
        const scaleMatch  = style.match(/transform\s*:\s*scale\(([^,)]+),([^)]+)\)/);
        const originMatch = style.match(/transform-origin\s*:\s*([\d.]+)px\s+([\d.]+)px/);
        if (!scaleMatch) return;

        const sx = parseFloat(scaleMatch[1]);
        const sy = parseFloat(scaleMatch[2]);
        let transform;
        if (originMatch) {
            const ox = parseFloat(originMatch[1]);
            const oy = parseFloat(originMatch[2]);
            transform = `translate(${ox},${oy}) scale(${sx},${sy}) translate(${-ox},${-oy})`;
        } else {
            transform = `scale(${sx},${sy})`;
        }
        const existing = el.getAttribute('transform');
        el.setAttribute('transform', existing ? `${existing} ${transform}` : transform);
        const cleanStyle = style
            .replace(/transform\s*:[^;]+;?/g, '')
            .replace(/transform-origin\s*:[^;]+;?/g, '')
            .trim();
        if (cleanStyle) el.setAttribute('style', cleanStyle);
        else            el.removeAttribute('style');
    });
}

/**
 * Lit les dimensions d'un SVG de façon défensive (viewBox peut être null).
 * Retourne les dimensions en unités natives du SVG (pixels abcjs).
 */
function _getSvgDims(svg) {
    const vb = svg.viewBox?.baseVal;
    if (vb && (vb.width || vb.height)) return { w: vb.width, h: vb.height };
    const aw = parseFloat(svg.getAttribute('width'))  || 0;
    const ah = parseFloat(svg.getAttribute('height')) || 0;
    if (aw || ah) return { w: aw, h: ah };
    const r = svg.getBoundingClientRect();
    return { w: r.width || STAFF_W_PX, h: r.height || 100 };
}

/**
 * Re-rend la partition avec oneSvgPerLine : un SVG par ligne de portée.
 * Retourne les SVG clonés (transforms convertis), prêts à paginer.
 *
 * @param {string} abc
 * @returns {SVGElement[]}
 */
function _renderSvgsForPrint(abc) {
    const wrap = document.createElement('div');
    wrap.style.cssText = `position:absolute;left:-9999px;top:-9999px;width:${STAFF_W_PX}px;visibility:hidden;`;
    document.body.appendChild(wrap);

    try {
        ABCJS.renderAbc(wrap, abc, {
            staffwidth:      STAFF_W_PX,
            responsive:      false,
            oneSvgPerLine:   true,   // ← un SVG par ligne de portée
            stretchLastLine: true,
            addClasses:      true,
            bagpipes:        /%%MIDI program\s+109/.test(abc),
        });

        return Array.from(wrap.querySelectorAll('svg')).map(svg => {
            const clone = svg.cloneNode(true);
            _convertCssTransforms(clone);
            return clone;
        });
    } finally {
        document.body.removeChild(wrap);
    }
}

/**
 * Regroupe les SVG (un par portée) en pages A4.
 * Conversion px → pt : 1px = 72/96 pt.
 *
 * @param {SVGElement[]} svgs
 * @returns {SVGElement[][]}
 */
function _paginateSvgs(svgs) {
    const pxToPt  = 72 / 96;
    const usableH = PAGE_H_PT - MARGIN_PT * 2;
    const pages   = [];
    let page  = [];
    let usedH = 0;

    for (const svg of svgs) {
        const { h } = _getSvgDims(svg);
        const hPt   = h * pxToPt;

        // Un SVG seul plus grand que la page → page dédiée
        if (hPt > usableH) {
            if (page.length) { pages.push(page); page = []; usedH = 0; }
            pages.push([svg]);
            continue;
        }

        if (usedH + hPt > usableH && page.length) {
            pages.push(page);
            page  = [];
            usedH = 0;
        }
        page.push(svg);
        usedH += hPt;
    }
    if (page.length) pages.push(page);
    return pages;
}

// ── PDF ───────────────────────────────────────────────────────────────────────

async function _buildPdf(abc) {
    const svgs = _renderSvgsForPrint(abc);
    if (!svgs.length) return null;

    const pxToPt = 72 / 96;
    const pages  = _paginateSvgs(svgs);
    const usableW = PAGE_W_PT - MARGIN_PT * 2;
    const usableH = PAGE_H_PT - MARGIN_PT * 2;

    const { jsPDF } = window.jspdf;
    const pdf  = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();

    for (let p = 0; p < pages.length; p++) {
        if (p > 0) pdf.addPage();

        let cursorY = MARGIN_PT;

        for (const svg of pages[p]) {
            const { w: srcW, h: srcH } = _getSvgDims(svg);
            const srcWpt = srcW * pxToPt;
            const srcHpt = srcH * pxToPt;

            // Mettre à l'échelle pour tenir dans la largeur utile
            const scale  = Math.min(usableW / srcWpt, (usableH - (cursorY - MARGIN_PT)) / srcHpt);
            const drawW  = srcWpt * scale;
            const drawH  = srcHpt * scale;
            const offX   = MARGIN_PT + (usableW - drawW) / 2;

            svg.setAttribute('width',  srcW);
            svg.setAttribute('height', srcH);
            svg.style.cssText = 'position:absolute;left:-9999px;top:-9999px;';
            document.body.appendChild(svg);
            try {
                await pdf.svg(svg, { x: offX, y: cursorY, width: drawW, height: drawH });
            } finally {
                document.body.removeChild(svg);
            }
            cursorY += drawH;
        }

        // Pied de page
        pdf.setPage(p + 1);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.setFont('helvetica', 'normal');
        const footerText = pages.length > 1
            ? `Partition réalisée avec Scoroù — https://scorou.com    •    Page ${p + 1} / ${pages.length}`
            : 'Partition réalisée avec Scoroù — https://scorou.com';
        pdf.text(
            footerText,
            pageW / 2,
            pageH - 10,
            { align: 'center' }
        );
    }

    return pdf;
}

// ── Print DOM ─────────────────────────────────────────────────────────────────

/**
 * Injecte les SVG paginés dans un #print-container visible uniquement
 * en @media print. Un SVG par portée → le navigateur ne coupe jamais
 * une portée en deux lors d'un saut de page.
 *
 * @param {string} abc
 */
export function printPartition(abc) {
    const svgs  = _renderSvgsForPrint(abc);
    const pages = _paginateSvgs(svgs);

    document.getElementById('print-container')?.remove();

    const container  = document.createElement('div');
    container.id     = 'print-container';
    const totalPages = pages.length;

    pages.forEach((pageSvgs, pageIdx) => {
        const pageDiv = document.createElement('div');
        pageDiv.className = 'print-page';
        if (pageIdx > 0) pageDiv.classList.add('print-page--break');

        pageSvgs.forEach(svg => {
            svg.style.cssText = '';
            svg.setAttribute('width',  '100%');
            svg.removeAttribute('height');
            pageDiv.appendChild(svg);
        });

        // Pied de page avec pagination
        const footer = document.createElement('div');
        footer.className   = 'print-page-footer';
        footer.textContent = totalPages > 1
            ? `Partition réalisée avec Scoroù — https://scorou.com    •    Page ${pageIdx + 1} / ${totalPages}`
            : 'Partition réalisée avec Scoroù — https://scorou.com';
        pageDiv.appendChild(footer);

        container.appendChild(pageDiv);
    });

    document.body.appendChild(container);

    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            window.print();
            const cleanup = () => {
                container.remove();
                window.removeEventListener('afterprint', cleanup);
            };
            window.addEventListener('afterprint', cleanup);
        });
    });
}

// ── API publique ──────────────────────────────────────────────────────────────

export async function generatePdfBlob(abc) {
    const pdf = await _buildPdf(abc);
    if (!pdf) return null;
    return pdf.output('blob');
}

export async function downloadPdf(abc) {
    const pdf = await _buildPdf(abc);
    if (!pdf) return;
    const titleEl = document.getElementById('partition-title-display');
    const title   = (titleEl?.textContent || 'partition').trim().replace(/\s+/g, '-').toLowerCase();
    pdf.save(`${title}.pdf`);
}

/**
 * Génère un PNG depuis le premier SVG de #notation et l'envoie à l'API.
 */
export async function generateAndSaveOgImage(partitionId, token) {
    const svg = document.querySelector('#notation svg');
    if (!svg) { console.error('[og] SVG introuvable'); return null; }

    const { w: svgW, h: svgH } = _getSvgDims(svg);
    const svgWf = svgW || 800;
    const svgHf = svgH || 600;

    const TARGET_W = 1200;
    const scale    = TARGET_W / svgWf;
    const canvasW  = Math.round(svgWf * scale);
    const canvasH  = Math.round(svgHf * scale);

    const clone = svg.cloneNode(true);
    clone.setAttribute('width',  svgWf);
    clone.setAttribute('height', svgHf);
    const resolveColor = el => {
        if (el.getAttribute('fill')   === 'currentColor') el.setAttribute('fill',   '#000000');
        if (el.getAttribute('stroke') === 'currentColor') el.setAttribute('stroke', '#000000');
        if (el.style?.fill   === 'currentColor') el.style.fill   = '#000000';
        if (el.style?.stroke === 'currentColor') el.style.stroke = '#000000';
    };
    resolveColor(clone);
    clone.querySelectorAll('*').forEach(resolveColor);

    const svgData = new XMLSerializer().serializeToString(clone);
    const blobUrl = URL.createObjectURL(new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' }));

    const pngB64 = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width  = canvasW;
            canvas.height = canvasH;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvasW, canvasH);
            ctx.scale(scale, scale);
            ctx.drawImage(img, 0, 0, svgWf, svgHf);
            URL.revokeObjectURL(blobUrl);
            resolve(canvas.toDataURL('image/png').split(',')[1]);
        };
        img.onerror = e => { URL.revokeObjectURL(blobUrl); reject(e); };
        img.src = blobUrl;
    });

    const res  = await fetch('/api/og-image.php', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body:    JSON.stringify({ partition_id: partitionId, png_b64: pngB64 }),
    });
    const data = await res.json();
    return data.url ?? null;
}

/**
 * Génère un PDF et l'envoie à l'API pour stockage serveur.
 */
export async function generateAndStorePdf(partitionId, token, slug, abc) {
    const blob = await generatePdfBlob(abc);
    if (!blob) return;

    const arrayBuffer = await blob.arrayBuffer();
    const bytes  = new Uint8Array(arrayBuffer);
    let   binary = '';
    bytes.forEach(b => binary += String.fromCharCode(b));
    const pdfB64 = btoa(binary);

    const res = await fetch('/api/pdf-store.php', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body:    JSON.stringify({
            partition_id: partitionId,
            pdf_b64:      pdfB64,
            pdf_filename: (slug || 'partition') + '.pdf',
        }),
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Erreur stockage PDF');
}

// ── ABC / MIDI ────────────────────────────────────────────────────────────────

function _exportFilename(ext) {
    const titleEl = document.getElementById('partition-title-display');
    const title   = (titleEl?.textContent || '').trim().replace(/\s+/g, '-').toLowerCase();
    return `${title || 'partition'}.${ext}`;
}

function _downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob);
    const a   = document.createElement('a');
    a.href     = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Exporte l'ABC tel qu'il est rendu (voix masquées exclues).
 * @param {string} abc — sortie de getAbcForRender()
 */
export function downloadAbc(abc) {
    if (!abc || !abc.trim()) return;
    // X: est obligatoire pour la plupart des logiciels ABC (EasyABC, abcm2ps…)
    const text = /^X:/m.test(abc) ? abc : `X:1\n${abc}`;
    _downloadBlob(
        new Blob([text.trimEnd() + '\n'], { type: 'text/plain;charset=utf-8' }),
        _exportFilename('abc')
    );
}

// ── MP3 ───────────────────────────────────────────────────────────────────────

/**
 * Ajoute les bourdons au rendu audio, avec le même graphe que drone.js
 * (dent de scie → passe-bas 900 Hz → gain 1.2 / 0.9 → master 0.016).
 */
async function _mixDrones(buffer, freqs) {
    const ctx = new OfflineAudioContext(buffer.numberOfChannels, buffer.length, buffer.sampleRate);

    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    src.start(0);

    const master = ctx.createGain();
    master.gain.value = 0.016;
    master.connect(ctx.destination);

    freqs.forEach((freq, i) => {
        const osc    = ctx.createOscillator();
        const filter = ctx.createBiquadFilter();
        const gain   = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.value = freq;
        filter.type = 'lowpass';
        filter.frequency.value = 900;
        gain.gain.value = i === 0 ? 1.2 : 0.9;
        osc.connect(filter);
        filter.connect(gain);
        gain.connect(master);
        osc.start(0);
    });

    return ctx.startRendering();
}

function _toInt16(float32) {
    const out = new Int16Array(float32.length);
    for (let i = 0; i < float32.length; i++) {
        const s = Math.max(-1, Math.min(1, float32[i]));
        out[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return out;
}

function _encodeMp3(buffer, kbps = 192) {
    const channels = Math.min(2, buffer.numberOfChannels);
    const left     = _toInt16(buffer.getChannelData(0));
    const right    = channels > 1 ? _toInt16(buffer.getChannelData(1)) : null;
    const encoder  = new Mp3Encoder(channels, buffer.sampleRate, kbps);
    const BLOCK    = 1152;
    const parts    = [];

    for (let i = 0; i < left.length; i += BLOCK) {
        const l   = left.subarray(i, i + BLOCK);
        const out = right
            ? encoder.encodeBuffer(l, right.subarray(i, i + BLOCK))
            : encoder.encodeBuffer(l);
        if (out.length) parts.push(out);
    }
    const end = encoder.flush();
    if (end.length) parts.push(end);

    return new Blob(parts, { type: 'audio/mpeg' });
}

/**
 * Exporte en MP3 le rendu audio de la partition affichée
 * (mêmes soundfonts et même sequenceCallback que la lecture).
 *
 * @param {object}   visualObj                 — objet abcjs du rendu courant (renderer.js)
 * @param {object}   opts
 * @param {Function} opts.sequenceCallback     — celui du dernier render()
 * @param {string}   opts.soundFontUrl
 * @param {number[]|null} opts.droneFreqs      — fréquences des bourdons, ou null
 */
export async function downloadMp3(visualObj, { sequenceCallback, soundFontUrl, droneFreqs = null } = {}) {
    if (!visualObj) return;

    const synth = new ABCJS.synth.CreateSynth();
    await synth.init({
        visualObj,
        options: { soundFontUrl, sequenceCallback },
    });
    await synth.prime();

    let buffer = synth.getAudioBuffer();
    if (!buffer) return;
    if (droneFreqs?.length) buffer = await _mixDrones(buffer, droneFreqs);

    _downloadBlob(_encodeMp3(buffer), _exportFilename('mp3'));
}
